import JSZip from "jszip";
import { unzip } from "fflate";
import { streamZipToFiles } from "./expandZipStreamed";
import { normalizeZipForJSZip } from "./normalizeZipForJSZip";

// Above this size we route through the streaming unzip path. The buffered
// JSZip / fflate-sync paths both load the whole archive into one
// ArrayBuffer, which fails near Chrome's ~2 GiB per-tab cap.
const STREAM_THRESHOLD_BYTES = 500 * 1024 * 1024;

/**
 * fflate-based fallback for ZIPs that JSZip can't handle
 * (e.g., ZIP64, Deflate64, or non-standard central directories).
 */
function expandWithFflate(buffer: ArrayBuffer): Promise<File[]> {
  return new Promise((resolve, reject) => {
    unzip(new Uint8Array(buffer), (err, data) => {
      if (err) return reject(err);

      const files: File[] = [];
      for (const [path, content] of Object.entries(data)) {
        // Skip folders (empty entries or paths ending with /)
        if (path.endsWith("/") || content.length === 0) continue;

        const baseName = path.split("/").pop() || path;
        const blob = new Blob([content.buffer as ArrayBuffer]);
        const file = new File([blob], baseName, { type: "" });

        // Tag with the ZIP-internal directory path (used by Firmex routing)
        const lastSlash = path.lastIndexOf("/");
        if (lastSlash > 0) {
          (file as any).__zipDir = path.substring(0, lastSlash);
          console.log(`   📦 Tagged "${baseName}" with zipDir="${(file as any).__zipDir}"`);
        }

        files.push(file);
      }
      resolve(files);
    });
  });
}

export async function expandZipFiles(input: File[]): Promise<File[]> {
  const out: File[] = [];

  for (let f of input) {
    const isZip = /\.zip$/i.test(f.name) || f.type === "application/zip";
    if (!isZip) {
      // Non-zip: pass through unchanged
      out.push(f);
      continue;
    }

    console.log("📦 Expanding ZIP:", f.name);

    // Intralinks archives carry a ZIP64 trailer that JSZip misreads as
    // prepended data; strip it (zero-copy) when present, no-op otherwise.
    f = await normalizeZipForJSZip(f);

    // Large archives can't fit into a single ArrayBuffer (Chrome caps at
    // ~2 GiB), so first try the streaming unzip path (memory-safe — peak memory
    // is roughly one entry). fflate's streaming Unzip can't handle ZIP64 /
    // data-descriptor archives, though; when it fails and the archive still
    // fits in memory, recover via buffered JSZip, which reads the central
    // directory and handles ZIP64.
    if (f.size > STREAM_THRESHOLD_BYTES) {
      console.log(
        `   ⚙️ Large archive (${(f.size / (1024 * 1024)).toFixed(0)} MB) — using streaming path`
      );
      try {
        const streamed = await streamZipToFiles(f);
        console.log(`✅ Streaming extracted ${streamed.length} file(s) from ${f.name}`);
        out.push(...streamed);
        continue;
      } catch (streamErr: any) {
        const reason = streamErr?.message ?? String(streamErr);
        // ~1.6 GB is about the most a single ArrayBuffer + decompression
        // overhead can take before Chrome's ~2 GB cap risks an OOM crash.
        const BUFFER_FALLBACK_LIMIT = 1600 * 1024 * 1024;
        if (f.size > BUFFER_FALLBACK_LIMIT) {
          console.error(`❌ Streaming ZIP failed for "${f.name}": ${reason}`, streamErr);
          throw new Error(`ZIP failed: ${reason} (too large to buffer as a fallback — needs a ZIP64 streaming reader)`);
        }
        console.warn(
          `⚠️ Streaming failed for "${f.name}" (${reason}); recovering via buffered JSZip (handles ZIP64)…`,
          streamErr
        );
        // fall through to the JSZip buffered path below
      }
    }

    try {
      const zip = await JSZip.loadAsync(f);
      const entries = Object.entries(zip.files);

      console.log(`Found ${entries.length} entries inside ${f.name}`);

      for (const [path, entry] of entries) {
        // ✅ Ignore all folders inside the zip
        if (entry.dir) continue;

        // ✅ Always strip folder hierarchy and keep just the filename
        const blob = await entry.async("blob");
        const baseName = path.split("/").pop() || path;

        const file = new File([blob], baseName, { type: blob.type || "" });

        // Tag with the ZIP-internal directory path (used by Firmex routing)
        const lastSlash = path.lastIndexOf("/");
        if (lastSlash > 0) {
          (file as any).__zipDir = path.substring(0, lastSlash);
          console.log(`   📦 Tagged "${baseName}" with zipDir="${(file as any).__zipDir}"`);
        }

        out.push(file);
      }

      console.log(`✅ Expanded ${out.length} total files so far.`);
    } catch (err) {
      // JSZip failed — try fflate as fallback
      console.warn(`⚠️ JSZip failed for "${f.name}", trying fflate fallback...`, err);

      try {
        const buffer = await f.arrayBuffer();
        const fallbackFiles = await expandWithFflate(buffer);
        console.log(`✅ fflate fallback extracted ${fallbackFiles.length} file(s) from ${f.name}`);
        out.push(...fallbackFiles);
      } catch (fallbackErr: any) {
        const reason = fallbackErr?.message ?? String(fallbackErr);
        console.error(
          `❌ Both JSZip and fflate failed for "${f.name}": ${reason}`,
          fallbackErr
        );
        throw new Error(`ZIP failed: ${reason}`);
      }
    }
  }

  return out;
}
